//@ts-nocheck
import ShellLayout from '../../layouts/ShellLayout/ShellLayout';
import shellHeaderBuilder from '../../utils/shellHeaderBuilder';
import LibraryNewItemSlide from '../../components/Trip/Library/LibraryNewItemSlide/LibraryNewItemSlide';
import LibraryDeleteContent from '../../components/Trip/Library/LibraryDeleteContent/LibraryDeleteContent';
import LibraryNewServiceSlide from '../../components/Trip/Library/LibraryNewServiceSlide/LibraryNewServiceSlide';
import LibraryItemSlideOut from '../../components/Trip/Library/LibraryItemSlideOut/LibraryItemSlideOut';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Box, Skeleton } from '@mui/material';
import { Trash, PlusCircle } from '@phosphor-icons/react';
import { XGButton, XGInputSearch, XGDropdownMenu } from 'xg-cl';
import { useSelector } from 'react-redux';
import { getContentByFF } from '../../services/library/content';
import { getContentTypes } from '../../services/contenttype/contenttype';
import { getServiceTypes } from '../../services/servicetype/servicetype';
import {
  getLibraryLineItems,
  createLineItem,
  updateLineItem,
  deleteLineItem,
  uploadLineItemImage,
  uploadLineItemAttachment,
  deleteLineItemImage,
  deleteLineItemAttachment
} from '../../services/lineitem/lineitem';
import { attachStockPhotoToLineItem } from '../../services/photo/lineItemStockPhoto';
import { ErrorToast } from '../../utils/alerts';
import {
  formatServiceTypeOptions,
  getServiceTypeIcon
} from '../../utils/serviceTypes/serviceTypes';
import { getAllLineItemServiceTypes } from '../../constants/lineItemServiceTypes';
import { LineItemEditSlideout } from '../../components/LineItem';
import * as PropTypes from 'prop-types';
import LibraryResults from './LibraryResults';
import {
  filterLibraryItems,
  getLibraryResultCounts,
  LIBRARY_GROUP_ORDER,
  MIN_LIBRARY_SEARCH_LENGTH
} from '../../utils/librarySearch';
import LibrarySearchInput from '../../components/LibrarySearchInput/LibrarySearchInput';
import LibraryTypeFilter from './LibraryTypeFilter';
import {
  getLibraryItemIdentity,
  getLibraryItemKey
} from '../../utils/libraryIdentity';
import './Library.css';

/**
 * This is the library page component.
 *
 * @namespace Pages
 */
const Library = ({ id }) => {
  const { t } = useTranslation(['trip', 'library']);
  const {
    showLibraryService,
    tripNavigation,
    itineraryNavigation,
    enableRefactoredServices,
    enableLibrarySearchEnhancements
  } = useFlags();
  const tenant = useSelector((state) => state.tenant);
  const [libraryContentTypes, setLibraryContentTypes] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [menuServiceTypes, setMenuServiceTypes] = useState([]);
  const [selectedLibraryContentType, setSelectedLibraryContentType] =
    useState(null);
  const [initialLibraryContent, setInitialLibraryContent] = useState([]);
  const [libraryContent, setLibraryContent] = useState([]);
  const [libraryLineItems, setLibraryLineItems] = useState([]);
  const [inputText, setInputText] = useState('');
  const [debouncedInputText, setDebouncedInputText] = useState('');
  const [selectedLibraryTypes, setSelectedLibraryTypes] = useState(() => [
    ...LIBRARY_GROUP_ORDER
  ]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [contentTypeSelected, setContentTypeSelected] = useState();
  const [openSlide, setOpenSlide] = useState(false);
  const [openServiceSlide, setOpenServiceSlide] = useState(false);
  const [openLineItemSlide, setOpenLineItemSlide] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [newFormServiceType, setNewFormServiceType] = useState();
  const [isLibraryContent, setIsLibraryContent] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedLineItem, setSelectedLineItem] = useState(null);
  const libraryContentListRef = useRef(null);
  const libraryLoadRequestRef = useRef(0);

  const scrollLibraryResultsToTop = useCallback(() => {
    const scrollToTop = (element) => {
      if (!element) return;

      if (typeof element.scrollTo === 'function') {
        element.scrollTo({ top: 0, left: 0 });
        return;
      }

      element.scrollTop = 0;
      element.scrollLeft = 0;
    };

    const contentList = libraryContentListRef.current;
    scrollToTop(contentList);
    scrollToTop(contentList?.closest('.shell-content-wrapper'));
  }, []);

  const setIdSelected = (nextId) => {
    if (!nextId) {
      setSelectedItem(null);
      return;
    }
    setSelectedItem({ kind: 'content', id: nextId });
  };

  const basicContentTypes = useMemo(
    () =>
      libraryContentTypes.length > 0
        ? libraryContentTypes.filter((content) => content.contenttypeid !== -1)
        : [],
    [libraryContentTypes]
  );

  const enhancedLibraryResults = useMemo(
    () => filterLibraryItems(initialLibraryContent, debouncedInputText),
    [initialLibraryContent, debouncedInputText]
  );

  const libraryResultCounts = useMemo(
    () => getLibraryResultCounts(enhancedLibraryResults),
    [enhancedLibraryResults]
  );
  const minimumSearchHint =
    inputText.trim().length > 0 &&
    inputText.trim().length < MIN_LIBRARY_SEARCH_LENGTH
      ? t('library:minimumSearchLength', {
          count: MIN_LIBRARY_SEARCH_LENGTH
        })
      : undefined;

  const handleClickButton = (event) => {
    const { name } = event.target;

    if (enableRefactoredServices && name === 'service') {
      setSelectedLibraryContentType({
        code: 'service',
        contenttypeid: -9999
      });
      setInputText('');
      return;
    }

    const contentType = libraryContentTypes?.find(
      (contentType) => contentType?.code === name
    );
    setSelectedLibraryContentType(
      contentType?.contenttypeid ? contentType : null
    );
    setInputText('');
  };

  const handleChangeInput = (event) => {
    const { value } = event.target;
    setInputText(value);
  };

  useEffect(() => {
    if (!enableLibrarySearchEnhancements || inputText.trim() === '') {
      setDebouncedInputText(inputText);
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setDebouncedInputText(inputText);
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [enableLibrarySearchEnhancements, inputText]);

  const handleSearch = () => {
    const text = inputText.trim().toLocaleLowerCase();

    if (text.length > 0 && text.length < MIN_LIBRARY_SEARCH_LENGTH) {
      if (
        enableRefactoredServices &&
        selectedLibraryContentType?.code === 'service'
      ) {
        setLibraryContent(libraryLineItems);
      } else if (selectedLibraryContentType) {
        setLibraryContent(
          initialLibraryContent.filter(
            (content) =>
              content?.contenttypeid ===
              selectedLibraryContentType.contenttypeid
          )
        );
      } else {
        setLibraryContent(initialLibraryContent);
      }
      return;
    }

    if (
      enableRefactoredServices &&
      selectedLibraryContentType?.code === 'service'
    ) {
      if (text.length > 0) {
        const filtered = libraryLineItems.filter((item) =>
          item?.title?.toLowerCase().includes(text)
        );
        setLibraryContent(filtered);
      } else {
        setLibraryContent(libraryLineItems);
      }
      return;
    }

    if (text.length > 0) {
      if (!selectedLibraryContentType) {
        const filteredContent = initialLibraryContent.filter((content) =>
          content?.title?.toLowerCase().includes(text)
        );
        setLibraryContent(filteredContent);
      } else {
        const filteredContent = initialLibraryContent.filter(
          (content) =>
            content?.title?.toLowerCase().includes(text) &&
            content?.contenttypeid === selectedLibraryContentType?.contenttypeid
        );
        setLibraryContent(filteredContent);
      }
    } else {
      setLibraryContent(initialLibraryContent);
    }
  };

  const handleSlideClose = () => {
    setOpenSlide(false);
    setOpenServiceSlide(false);
    setOpenLineItemSlide(false);
    setSelectedItem(null);
    setSelectedLineItem(null);
  };

  const handleNewitem = (event, option) => {
    if (enableRefactoredServices && option?.kind === 'lineitem') {
      setSelectedItem({ kind: 'lineitem', id: 0 });
      setSelectedLineItem({ servicetypecode: option.code });
      setOpenLineItemSlide(true);
      setOpenSlide(false);
      setOpenServiceSlide(false);
      setIsEditing(false);
      return;
    }

    let foundOption = basicContentTypes.filter(
      (cType) => cType.contenttypeid === option.contenttypeid
    )[0];

    if (itineraryNavigation) {
      foundOption = foundOption || option;
    }

    toggleSlide(option);
    setContentTypeSelected(foundOption);
    setIsEditing(false);
  };

  const handleDelete = async (item) => {
    if (enableRefactoredServices && item?.kind === 'lineitem') {
      const ok = window.confirm(t('library:deleteConfirmation'));
      if (!ok) return;

      try {
        await deleteLineItem(item.lineitemid);
        await fetchData();
      } catch (e) {
        ErrorToast(t('library:errorDeletingContent'));
      }
      return;
    }

    setSelectedItem(getLibraryItemIdentity(item));
    setOpenModal(true);
    setIsLibraryContent(item.isservice || !!item.itineraryserviceid);
  };

  const handleOnItemDeleted = (id, kind) => {
    const contentWitoutItem = initialLibraryContent.filter(
      (item) => getLibraryItemKey(item) !== `${kind}-${id}`
    );
    setOpenSlide(false);
    setSelectedItem(null);
    setInitialLibraryContent(contentWitoutItem);
  };

  const toggleSlide = (content) => {
    const isLibrary = content.isservice || !!content.servicetypeid;
    if (isLibrary) {
      setOpenSlide(false);
      setOpenServiceSlide(true);
      setOpenLineItemSlide(false);
      setNewFormServiceType(content);
    } else {
      setOpenServiceSlide(false);
      setOpenLineItemSlide(false);
      setOpenSlide(true);
    }
    setIsLibraryContent(isLibrary);
  };

  const handleClickOnContent = (content) => {
    if (enableRefactoredServices && content?.kind === 'lineitem') {
      setSelectedItem({ kind: 'lineitem', id: content.lineitemid });
      setSelectedLineItem(content);
      setOpenLineItemSlide(true);
      setOpenSlide(false);
      setOpenServiceSlide(false);
      setIsEditing(true);
      return;
    }

    let founContent = basicContentTypes?.filter(
      (cType) => cType.contenttypeid === content.contenttypeid
    )[0];

    if (itineraryNavigation) {
      founContent = founContent || content;
    }

    setSelectedItem(getLibraryItemIdentity(content));
    setContentTypeSelected(founContent);
    toggleSlide(content);
    setIsEditing(true);
  };

  // Update page title
  useEffect(() => {
    document.title = 'Nivo - Library';
  }, []);

  //Set library content on content type change
  useEffect(() => {
    const contentCode = selectedLibraryContentType?.code;
    const contenttypeid = selectedLibraryContentType?.contenttypeid;

    if (!contenttypeid) {
      setLibraryContent(initialLibraryContent);
      return;
    }

    // When using refactored services, the "service" tab is lineitems, not content
    if (enableRefactoredServices && contentCode === 'service') {
      setLibraryContent(libraryLineItems);
      return;
    }

    const filteredLibraryContent = initialLibraryContent.filter(
      (content) => content.contenttypeid === contenttypeid
    );

    setLibraryContent(filteredLibraryContent);
  }, [
    selectedLibraryContentType,
    initialLibraryContent,
    enableRefactoredServices,
    libraryLineItems
  ]);

  const fetchData = useCallback(async () => {
    const requestId = ++libraryLoadRequestRef.current;
    setLoading(true);
    setLoadError(false);
    try {
      const shouldUseRefactored = Boolean(enableRefactoredServices);

      const results = await Promise.all([
        getContentTypes(),
        getServiceTypes(),
        // Refactored services use the content endpoint plus the line item endpoint.
        getContentByFF(shouldUseRefactored ? false : showLibraryService),
        shouldUseRefactored
          ? getLibraryLineItems()
          : Promise.resolve({ data: [] })
      ]);

      // A flag change or a later refresh must not be overwritten by an older load.
      if (requestId !== libraryLoadRequestRef.current) return;

      // Keep the previous complete load if any response is unavailable.
      if (results.some((result) => !Array.isArray(result?.data))) {
        setLoadError(true);
        return;
      }
      const [contentTypes, serviceTypes, content, rawLineItems] = results.map(
        (result) => result.data
      );
      const libraryContentModified = content.map((object) => {
        // Resolve group codes from this load, never from a stale render snapshot.
        const contentType = contentTypes.find(
          (ct) => ct.contenttypeid === object.contenttypeid
        );
        return {
          ...object,
          images: object.contentimage,
          code:
            object.contenttype?.code || contentType?.code || object.code || null
        };
      });

      const lineItemsForList = rawLineItems.map((li) => ({
        ...li,
        kind: 'lineitem',
        contenttypeid: -9999,
        code: 'service'
      }));

      const combinedAll = shouldUseRefactored
        ? [...lineItemsForList, ...libraryContentModified]
        : libraryContentModified;

      setLibraryContentTypes(contentTypes);
      setLibraryLineItems(lineItemsForList);
      setInitialLibraryContent(combinedAll);
      setLibraryContent(combinedAll);
      setServiceTypes(serviceTypes);
    } catch (error) {
      if (requestId !== libraryLoadRequestRef.current) return;
      setLoadError(true);
    } finally {
      if (requestId === libraryLoadRequestRef.current) setLoading(false);
    }
  }, [showLibraryService, enableRefactoredServices]);

  //Build menu service types when content types or service types change
  useEffect(() => {
    if (basicContentTypes.length === 0 || serviceTypes.length === 0) {
      return;
    }

    const menuTypes = basicContentTypes.map((cType) => ({
      ...cType,
      name: cType.title,
      iconref: cType.code === 'day' ? 'Sun' : 'File'
    }));

    if (menuTypes[menuTypes.length - 1]) {
      menuTypes[menuTypes.length - 1].divider = true;
    }

    if (enableRefactoredServices) {
      const lineItemTypes = getAllLineItemServiceTypes().map((st) => ({
        ...st,
        kind: 'lineitem'
      }));
      setMenuServiceTypes([...menuTypes, ...lineItemTypes]);
      return;
    }

    if (showLibraryService) {
      setMenuServiceTypes([...menuTypes, ...serviceTypes]);
    } else {
      setMenuServiceTypes(menuTypes);
    }
  }, [
    basicContentTypes,
    serviceTypes,
    showLibraryService,
    enableRefactoredServices
  ]);

  //Fetch library content
  useEffect(() => {
    const requestTracker = libraryLoadRequestRef;
    fetchData();
    return () => {
      requestTracker.current++;
    };
  }, [fetchData]);

  const handleOnSaveLibraryService = () => {
    fetchData();
    setOpenServiceSlide(false);
  };

  const handleOnDeleteLibraryService = (id) => {
    fetchData();
    setOpenServiceSlide(false);
    handleOnItemDeleted(id, 'service');
    setSelectedItem(null);
  };

  const handleSaveLibraryLineItem = async (formData) => {
    try {
      // Separate File objects (new uploads) from existing database records
      const newImages = (formData.images || [])
        .map((image, index) => ({ image, seq: index + 1 }))
        .filter(({ image }) => image instanceof File || image?.stockPhoto);
      const existingImages = (formData.images || []).filter(
        (img) => !(img instanceof File) && !img?.stockPhoto
      );
      const newAttachments = (formData.attachments || []).filter(
        (att) => att instanceof File
      );
      const existingAttachments = (formData.attachments || []).filter(
        (att) => !(att instanceof File)
      );

      // Remove images/attachments from formData - we'll handle them separately
      // eslint-disable-next-line no-unused-vars
      const { images, attachments, ...lineItemData } = formData;

      let savedLineItem;

      if (selectedLineItem?.lineitemid) {
        // Update existing library line item
        const response = await updateLineItem(
          selectedLineItem.lineitemid,
          lineItemData
        );
        savedLineItem = response?.data;

        // Detect deleted images by comparing original with current
        const originalImages = selectedLineItem.lineitemimage || [];
        const currentImageIds = existingImages
          .map((img) => img.lineitemimageid)
          .filter(Boolean);
        const deletedImages = originalImages.filter(
          (img) => !currentImageIds.includes(img.lineitemimageid)
        );

        // Delete removed images
        for (const image of deletedImages) {
          try {
            await deleteLineItemImage(image.lineitemimageid);
          } catch (error) {
            console.error('Error deleting image:', image.imagename, error);
          }
        }

        // Detect deleted attachments by comparing original with current
        const originalAttachments = selectedLineItem.lineitemattachment || [];
        const currentAttachmentIds = existingAttachments
          .map((att) => att.lineitemattachmentid)
          .filter(Boolean);
        const deletedAttachments = originalAttachments.filter(
          (att) => !currentAttachmentIds.includes(att.lineitemattachmentid)
        );

        // Delete removed attachments
        for (const attachment of deletedAttachments) {
          try {
            await deleteLineItemAttachment(attachment.lineitemattachmentid);
          } catch (error) {
            console.error(
              'Error deleting attachment:',
              attachment.attachmentname,
              error
            );
          }
        }
      } else {
        // Create new library line item
        const payload = {
          ...lineItemData,
          tripdayid: null
        };
        const { data } = await createLineItem(payload);
        savedLineItem = data;
      }

      if (!savedLineItem) {
        throw new Error('Failed to save library line item');
      }

      // Store new images in their selection order.
      if (newImages.length > 0) {
        for (const { image, seq } of newImages) {
          try {
            if (image.stockPhoto) {
              await attachStockPhotoToLineItem({
                photo: image.stockPhoto,
                lineItemId: savedLineItem.lineitemid,
                seq,
                isLibrary: true
              });
            } else {
              await uploadLineItemImage(savedLineItem.lineitemid, image, seq);
            }
          } catch (error) {
            ErrorToast(t('errors:imageUploadFailed'));
            throw error;
          }
        }
      }

      // Upload new attachments
      if (newAttachments.length > 0) {
        for (const file of newAttachments) {
          try {
            await uploadLineItemAttachment(savedLineItem.lineitemid, file);
          } catch (error) {
            console.error('Error uploading attachment:', file.name, error);
          }
        }
      }

      // Refresh the library list to show updated data
      await fetchData();
    } catch (e) {
      console.error('Error saving library line item:', e);
      throw e;
    }
  };

  const isLibraryUnavailable = loadError && libraryContent.length === 0;

  const mainHeader = shellHeaderBuilder({
    title: {
      title: t('trip:library')
    }
  });

  const renderLibraryItem = (item) => (
    <li
      key={getLibraryItemKey(item)}
      data-testid='library-content-item'
      id={getLibraryItemKey(item)}
      className={`library-content-item${selectedItem && getLibraryItemKey(item) === `${selectedItem.kind}-${selectedItem.id}` ? '-selected' : ''}`}
      onClick={() => {
        handleClickOnContent(item);
      }}
    >
      <span className='text-md-semibold text-ellipsis '>
        {item.title || t('library:untitled')}
      </span>
      <div className='library_content_item_icon'>
        <Trash
          size={20}
          color='#667085'
          className='library_icon'
          onClick={(event) => {
            event.stopPropagation();
            handleDelete(item);
          }}
        />
        {getServiceTypeIcon(item)}
      </div>
    </li>
  );

  return (
    <div id={id} data-testid='library-page'>
      <ShellLayout
        id='global-settings-shell'
        data-testid='global-settings-shell'
        headerData={mainHeader}
        isLoadingContent={false}
      >
        <div className='library__wrapper'>
          <div className='library-input-search-container'>
            <div className='library-search-control'>
              {enableLibrarySearchEnhancements ? (
                <LibrarySearchInput
                  name='content-title'
                  placeholder={t('trip:search')}
                  id='content-title-input'
                  value={inputText}
                  hint={minimumSearchHint}
                  clearLabel={t('library:clearSearch')}
                  onChange={handleChangeInput}
                  onClear={() => setInputText('')}
                />
              ) : (
                <XGInputSearch
                  name='content-title'
                  placeholder={t('trip:search')}
                  id='content-title-input'
                  type='text'
                  value={inputText}
                  hint={minimumSearchHint}
                  onChange={handleChangeInput}
                  onClick={handleSearch}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                />
              )}
            </div>
            {enableLibrarySearchEnhancements && (
              <LibraryTypeFilter
                counts={libraryResultCounts}
                selectedTypes={selectedLibraryTypes}
                onChange={setSelectedLibraryTypes}
              />
            )}
            <span className='library-control-row-spacer' aria-hidden='true' />
            <div
              data-testid='library-page-new-item-button'
              className='library-page-new-item-button'
            >
              <XGDropdownMenu
                id='library-page-new-item-button'
                isButton
                btnType='secondary'
                btnVariant='outlined'
                btnSize='medium'
                btnStartIcon={<PlusCircle size={20} />}
                btnText={t('library:newItem')}
                options={formatServiceTypeOptions(
                  menuServiceTypes,
                  handleNewitem
                )}
              />
            </div>
          </div>
          {!enableLibrarySearchEnhancements && (
            <Box
              className={`library-buttons-container ${tripNavigation ? 'library-buttons-container--fixed' : ''}`}
            >
              <XGButton
                data-testid='library-page-button'
                className={`library-page-button${selectedLibraryContentType === null ? '-active' : ''}`}
                id='library-page-button'
                name='all'
                text={t('library:all')}
                type='primary'
                size='medium'
                variant='contained'
                onClick={handleClickButton}
              />
              <XGButton
                data-testid='library-page-button'
                className={`library-page-button${selectedLibraryContentType?.code === 'day' ? '-active' : ''}`}
                id='library-page-button'
                name='day'
                text={t('library:days')}
                type='primary'
                size='medium'
                variant='contained'
                onClick={handleClickButton}
              />
              <XGButton
                data-testid='library-page-button'
                className={`library-page-button${selectedLibraryContentType?.code === 'additional' ? '-active' : ''}`}
                id='library-page-button'
                name='additional'
                text={t('library:content')}
                type='primary'
                size='medium'
                variant='contained'
                onClick={handleClickButton}
              />
              {(showLibraryService || enableRefactoredServices) && (
                <XGButton
                  data-testid='library-page-button'
                  className={`library-page-button${selectedLibraryContentType?.code === 'service' ? '-active' : ''}`}
                  id='library-page-button'
                  name='service'
                  text={t('library:service')}
                  type='primary'
                  size='medium'
                  variant='contained'
                  onClick={handleClickButton}
                />
              )}
            </Box>
          )}

          {loadError && (
            <div role='alert' className='library-load-error'>
              <span>{t('library:loadError')}</span>
              <XGButton
                id='library-retry-loading'
                text={t('library:retryLoading')}
                type='secondary'
                size='small'
                variant='outlined'
                onClick={fetchData}
              />
            </div>
          )}

          <div
            className={`library-content-list ${enableLibrarySearchEnhancements ? 'library-content-list--enhanced ' : ''}${tripNavigation ? 'library-content-list--fixed ' : ''}`}
            ref={libraryContentListRef}
          >
            {loading && libraryContent.length === 0 ? (
              <Box>
                {Array.from(new Array(10)).map((_, index) => (
                  <Box
                    key={index}
                    display='flex'
                    alignItems='center'
                    mb={2}
                    className='skeleton-item'
                  >
                    <Skeleton
                      variant='rectangular'
                      width='100%'
                      height='76px'
                      sx={{ marginRight: '10px', borderRadius: '8px' }}
                    />
                  </Box>
                ))}
              </Box>
            ) : isLibraryUnavailable ? null : enableLibrarySearchEnhancements ? (
              <LibraryResults
                items={enhancedLibraryResults}
                query={debouncedInputText}
                selectedTypes={selectedLibraryTypes}
                onSelectTypes={setSelectedLibraryTypes}
                onClearSearch={() => setInputText('')}
                onResultsChange={scrollLibraryResultsToTop}
                renderItem={renderLibraryItem}
              />
            ) : libraryContent && libraryContent.length > 0 ? (
              libraryContent.map(renderLibraryItem)
            ) : (
              <div className='no-content-found'>
                <span className='text-muted--secondary'>
                  {t('library:noResultsFor')} {`"${inputText}"`}
                </span>
              </div>
            )}
          </div>
        </div>
      </ShellLayout>

      {libraryContentTypes &&
        (itineraryNavigation ? (
          <LibraryItemSlideOut
            openSlide={openSlide}
            handleSlideClose={handleSlideClose}
            libraryContentTypes={basicContentTypes}
            handleOnItemAdded={setInitialLibraryContent}
            initialContent={initialLibraryContent}
            idSelected={selectedItem?.kind === 'content' ? selectedItem?.id : 0}
            handleDelete={handleDelete}
            contentTypeSelected={contentTypeSelected}
            isEditing={isEditing}
          />
        ) : (
          <LibraryNewItemSlide
            openSlide={openSlide}
            handleSlideClose={handleSlideClose}
            libraryContentTypes={basicContentTypes}
            handleOnItemAdded={setInitialLibraryContent}
            initialContent={initialLibraryContent}
            idSelected={selectedItem?.kind === 'content' ? selectedItem?.id : 0}
            handleDelete={handleDelete}
            contentTypeSelected={contentTypeSelected}
          />
        ))}

      {libraryContentTypes &&
        openServiceSlide &&
        !enableRefactoredServices &&
        serviceTypes.length &&
        tenant && (
          <LibraryNewServiceSlide
            openServiceSlide={openServiceSlide}
            handleServiceSlideClose={handleSlideClose}
            idSelected={selectedItem?.kind === 'service' ? selectedItem?.id : 0}
            serviceTypes={serviceTypes}
            newFormServiceType={newFormServiceType}
            onSave={handleOnSaveLibraryService}
            onDelete={handleOnDeleteLibraryService}
            tenantId={tenant?.tenantid}
            contentTypeSelected={contentTypeSelected}
            refreshList={fetchData}
          />
        )}

      {enableRefactoredServices && tenant && (
        <LineItemEditSlideout
          open={openLineItemSlide}
          onClose={() => {
            setOpenLineItemSlide(false);
            setSelectedLineItem(null);
            setSelectedItem(null);
          }}
          lineItem={selectedLineItem}
          onSave={handleSaveLibraryLineItem}
          onDelete={async (lineitemid) => {
            // Reuse existing delete flow so list refresh + selection clearing stays consistent
            await handleDelete({ kind: 'lineitem', lineitemid });
          }}
          tripId={null}
          tenantId={tenant?.tenantid}
          isLibrary={true}
        />
      )}

      <LibraryDeleteContent
        contentid={
          selectedItem && selectedItem.kind !== 'lineitem' ? selectedItem.id : 0
        }
        setIdSelected={setIdSelected}
        handleOnItemDeleted={handleOnItemDeleted}
        isOpenModal={openModal}
        updateIsOpen={setOpenModal}
        openSlide={openSlide}
        setOpenSlide={setOpenSlide}
        isLibrary={isLibraryContent}
      />
    </div>
  );
};

Library.propTypes = {
  id: PropTypes.string.isRequired
};

export default Library;
